import { useState, useEffect } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import client from '../api/client';

const LEVEL_STYLE: Record<string, any> = {
  저관여: { color: '#00D4AA', bg: '#0F2A22', label: '🟢 저관여' },
  중간관여: { color: '#F6AD55', bg: '#2A1F0D', label: '🟡 중간관여' },
  고관여: { color: '#F56565', bg: '#2A0D0D', label: '🔴 고관여' },
};

const CHANNELS = ['스마트스토어', '쿠팡', '11번가', 'G마켓', '자사몰'];

export default function Marketing() {
  const checkAuth = useAuthGuard();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'list' | 'saved'>('list');
  const [involvements, setInvolvements] = useState<any[]>([]);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [reviewCount, setReviewCount] = useState('');
  const [monthlyRevenue, setMonthlyRevenue] = useState('');
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  // 플레이스 전용
  const [visitorReview, setVisitorReview] = useState('');
  const [blogReview, setBlogReview] = useState('');
  const [placeRank, setPlaceRank] = useState('');
  const [monthlyVisitors, setMonthlyVisitors] = useState('');

  const loadData = async () => {
    try {
      const [inv, strat] = await Promise.all([
        client.get('/involvement/list'),
        client.get('/marketing/list'),
      ]);
      setInvolvements(inv.data.results);
      setStrategies(strat.data.strategies);
    } catch {}
  };

  useEffect(() => { loadData(); }, []);

  const toggleChannel = (ch: string) => {
    setSelectedChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  const handleSelect = (inv: any) => {
    setSelected(inv);
    setResult(null);
    setReviewCount('');
    setMonthlyRevenue('');
    setSelectedChannels([]);
    setVisitorReview('');
    setBlogReview('');
    setPlaceRank('');
    setMonthlyVisitors('');
  };

  const isPlace = selected?.store_type === '네이버 플레이스';

  const handleGenerate = async () => {
    if (!checkAuth()) return;
    if (!selected) return;
    if (!isPlace && selectedChannels.length === 0) return alert('판매 채널을 선택해주세요');
    setLoading(true);
    try {
      const res = await client.post('/marketing/save', {
        involvement_id: selected.id,
        title: selected.title,
        level: selected.level,
        avg_score: selected.avg_score,
        price_score: selected.price_score,
        frequency_score: selected.frequency_score,
        search_score: selected.search_score,
        risk_score: selected.risk_score,
        identity_score: selected.identity_score,
        brand_score: selected.brand_score || 5,
        review_count: Number(reviewCount) || 0,
        monthly_revenue: Number(monthlyRevenue) || 0,
        channels: isPlace ? '네이버 플레이스' : selectedChannels.join(', '),
        is_place: isPlace,
        visitor_review: Number(visitorReview) || 0,
        blog_review: Number(blogReview) || 0,
        place_rank: Number(placeRank) || 0,
        monthly_visitors: Number(monthlyVisitors) || 0,
      });
      setResult(res.data.strategy);
      await loadData();
    } catch { alert('생성 실패'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제할까요?')) return;
    try { await client.delete(`/marketing/${id}`); await loadData(); } catch {}
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
    input: { background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 14, width: '100%', boxSizing: 'border-box' },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '7px 16px', color: '#8892A4', fontSize: 12, cursor: 'pointer' },
    tab: (active: boolean) => ({ background: active ? '#1A2035' : 'none', border: active ? '0.5px solid #00D4AA' : '0.5px solid #2A3045', borderRadius: 6, padding: '7px 20px', color: active ? '#00D4AA' : '#8892A4', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400 }),
    label: { color: '#B0BAC8', fontSize: 12, marginBottom: 6, display: 'block' },
  };

  const StrategyView = ({ st }: { st: any }) => (
    <div>
      {st.channel_tips?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#B0BAC8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📢 채널별 전략</div>
          {st.channel_tips.map((tip: string, i: number) => (
            <div key={i} style={{ background: '#0F1117', borderRadius: 6, padding: '10px 14px', marginBottom: 6, color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{tip}</div>
          ))}
        </div>
      )}
      {st.viral_strategy && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#F56565', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{st.viral_strategy.title}</div>
          {st.viral_strategy.items.map((item: string, i: number) => (
            <div key={i} style={{ display: 'flex', gap: 10, background: '#1A0D0D', border: '0.5px solid #F5656533', borderRadius: 6, padding: '10px 14px', marginBottom: 6, alignItems: 'flex-start' }}>
              <span style={{ color: '#F56565', flexShrink: 0 }}>🔥</span>
              <span style={{ color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{item}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: '#B0BAC8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>⭐ 리뷰 전략 ({st.review_phase} 단계)</div>
        <div style={{ background: '#0F1117', borderRadius: 6, padding: '10px 14px', color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{st.review_strategy}</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: '#B0BAC8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>💰 광고 예산 배분</div>
        <div style={{ background: '#0F1117', borderRadius: 6, padding: '10px 14px', color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{st.budget_plan}</div>
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: '#B0BAC8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>✅ 즉시 실행 체크리스트</div>
        {st.checklist?.map((item: string, i: number) => (
          <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '0.5px solid #1E2535', alignItems: 'flex-start' }}>
            <span style={{ color: '#00D4AA', fontSize: 13, flexShrink: 0 }}>☐</span>
            <span style={{ color: '#E8ECF4', fontSize: 13, lineHeight: 1.6 }}>{item}</span>
          </div>
        ))}
      </div>
      <div style={{ background: '#0F1117', borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ color: '#E8ECF4', fontSize: 13, fontWeight: 600 }}>전문 마케팅 대행이 필요하신가요?</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 2 }}>바이럴·광고·콘텐츠 마케팅 전문 상담</div>
        </div>
        <button onClick={() => alert('카카오톡 오픈채팅 URL을 설정해주세요')}
          style={{ background: '#FEE500', border: 'none', borderRadius: 8, padding: '10px 20px', color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
          💬 마케팅 문의하기
        </button>
      </div>
    </div>
  );

  return (
    <Layout>
      <div style={{ maxWidth: 800 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>마케팅 전략</div>
          <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>관여도 진단 결과를 바탕으로 맞춤 마케팅 전략을 생성합니다</div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button style={s.tab(tab === 'list')} onClick={() => { setTab('list'); setSelected(null); setResult(null); }}>전략 생성</button>
          <button style={s.tab(tab === 'saved')} onClick={() => setTab('saved')}>저장된 전략 ({strategies.length})</button>
        </div>

        {tab === 'list' && (
          <>
            {/* 관여도 진단 목록 */}
            {!selected && (
              <>
                {involvements.length === 0 ? (
                  <div style={{ ...s.card, textAlign: 'center', padding: '60px 40px' }}>
                    <div style={{ fontSize: 32, marginBottom: 16 }}>📋</div>
                    <div style={{ color: '#F6AD55', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>⚠️ 먼저 Step 1. 관여도 진단을 완료해주세요</div>
                    <div style={{ color: '#8892A4', fontSize: 13, marginBottom: 24 }}>관여도 진단 결과를 바탕으로 맞춤 마케팅 전략을 생성합니다</div>
                    <button style={s.btn} onClick={() => navigate('/involvement')}>Step 1. 관여도 진단 하러가기 →</button>
                  </div>
                ) : (
                  <>
                    <div style={{ color: '#B0BAC8', fontSize: 13, marginBottom: 12 }}>관여도 진단 결과를 선택하세요</div>
                    {involvements.map(inv => {
                      const st = LEVEL_STYLE[inv.level];
                      return (
                        <div key={inv.id} style={{ ...s.card, cursor: 'pointer', transition: 'border 0.2s' }}
                          onClick={() => handleSelect(inv)}
                          onMouseEnter={e => (e.currentTarget.style.border = `0.5px solid ${st.color}`)}
                          onMouseLeave={e => (e.currentTarget.style.border = '0.5px solid #2A3045')}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{inv.title}</div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <span style={{ background: st.color + '22', border: `1px solid ${st.color}55`, borderRadius: 6, padding: '2px 10px', color: st.color, fontSize: 12, fontWeight: 600 }}>{st.label}</span>
                                <span style={{ color: '#8892A4', fontSize: 12 }}>평균 {inv.avg_score}점</span>
                                <span style={{ color: '#4A5568', fontSize: 11 }}>{new Date(inv.created_at).toLocaleDateString('ko-KR')}</span>
                              </div>
                            </div>
                            <span style={{ color: '#00D4AA', fontSize: 13 }}>선택 →</span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}

            {/* 추가 정보 입력 */}
            {selected && (
              <>
                <div style={{ ...s.card, background: LEVEL_STYLE[selected.level]?.bg, border: `1px solid ${LEVEL_STYLE[selected.level]?.color}33`, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{selected.title}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <span style={{ background: LEVEL_STYLE[selected.level]?.color + '22', border: `1px solid ${LEVEL_STYLE[selected.level]?.color}`, borderRadius: 6, padding: '2px 10px', color: LEVEL_STYLE[selected.level]?.color, fontSize: 12, fontWeight: 600 }}>{LEVEL_STYLE[selected.level]?.label}</span>
                        <span style={{ color: '#8892A4', fontSize: 12 }}>평균 {selected.avg_score}점</span>
                      </div>
                    </div>
                    <button style={s.btnGhost} onClick={() => setSelected(null)}>다시 선택</button>
                  </div>
                </div>

                <div style={s.card}>
                  <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>추가 정보 입력</div>
                  {isPlace ? (
                    <>
                      <div style={{ background: '#0F2A22', border: '0.5px solid #00D4AA33', borderRadius: 6, padding: '8px 12px', marginBottom: 14, color: '#00D4AA', fontSize: 12 }}>
                        📍 네이버 플레이스 전용 전략을 생성합니다
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                        <div>
                          <label style={s.label}>방문자 리뷰 수 (네이버 플레이스)</label>
                          <input style={s.input} type="number" value={visitorReview} onChange={e => setVisitorReview(e.target.value)} placeholder="예) 21" />
                        </div>
                        <div>
                          <label style={s.label}>블로그 리뷰 수</label>
                          <input style={s.input} type="number" value={blogReview} onChange={e => setBlogReview(e.target.value)} placeholder="예) 105" />
                        </div>
                        <div>
                          <label style={s.label}>주요 키워드 현재 순위</label>
                          <input style={s.input} type="number" value={placeRank} onChange={e => setPlaceRank(e.target.value)} placeholder="예) 14 (위)" />
                        </div>
                        <div>
                          <label style={s.label}>월 방문객 수 (예상)</label>
                          <input style={s.input} type="number" value={monthlyVisitors} onChange={e => setMonthlyVisitors(e.target.value)} placeholder="예) 300" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                        <div>
                          <label style={s.label}>현재 리뷰 수</label>
                          <input style={s.input} type="number" value={reviewCount} onChange={e => setReviewCount(e.target.value)} placeholder="예) 45" />
                        </div>
                        <div>
                          <label style={s.label}>현재 월 매출 (원)</label>
                          <input style={s.input} type="number" value={monthlyRevenue} onChange={e => setMonthlyRevenue(e.target.value)} placeholder="예) 3000000" />
                        </div>
                      </div>
                      <label style={s.label}>판매 채널 (복수 선택)</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {CHANNELS.map(ch => (
                          <button key={ch} onClick={() => toggleChannel(ch)} style={{
                            background: selectedChannels.includes(ch) ? '#1A2035' : 'none',
                            border: `1px solid ${selectedChannels.includes(ch) ? '#00D4AA' : '#2A3045'}`,
                            borderRadius: 6, padding: '6px 14px',
                            color: selectedChannels.includes(ch) ? '#00D4AA' : '#8892A4',
                            fontSize: 13, cursor: 'pointer'
                          }}>{ch}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {!result && (
                  <button style={s.btn} onClick={handleGenerate} disabled={loading}>
                    {loading ? '생성 중...' : '마케팅 전략 생성'}
                  </button>
                )}
              </>
            )}

            {/* 결과 */}
            {result && selected && (
              <div style={{ ...s.card, background: LEVEL_STYLE[selected.level]?.bg, border: `1px solid ${LEVEL_STYLE[selected.level]?.color}33` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <span style={{ background: LEVEL_STYLE[selected.level]?.color + '22', border: `1px solid ${LEVEL_STYLE[selected.level]?.color}`, borderRadius: 6, padding: '4px 14px', color: LEVEL_STYLE[selected.level]?.color, fontSize: 14, fontWeight: 700 }}>{LEVEL_STYLE[selected.level]?.label}</span>
                  <span style={{ color: '#B0BAC8', fontSize: 13 }}>{selected.title} 마케팅 전략</span>
                </div>
                <StrategyView st={result} />
                <button style={{ ...s.btnGhost, marginTop: 16 }} onClick={() => { setSelected(null); setResult(null); }}>← 다른 진단으로 전략 생성</button>
              </div>
            )}
          </>
        )}

        {tab === 'saved' && (
          <div>
            {strategies.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', color: '#4A5568', padding: 40 }}>저장된 전략이 없습니다</div>
            ) : strategies.map(r => {
              const st = LEVEL_STYLE[r.level];
              const isOpen = expanded === r.id;
              return (
                <div key={r.id} style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isOpen ? 16 : 0 }}>
                    <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => setExpanded(isOpen ? null : r.id)}>
                      <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{r.title}</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ background: st?.color + '22', border: `1px solid ${st?.color}55`, borderRadius: 6, padding: '2px 10px', color: st?.color, fontSize: 12, fontWeight: 600 }}>{st?.label}</span>
                        {r.channels && <span style={{ color: '#8892A4', fontSize: 12 }}>{r.channels}</span>}
                        {r.review_count > 0 && <span style={{ color: '#8892A4', fontSize: 12 }}>리뷰 {r.review_count}개</span>}
                        <span style={{ color: '#4A5568', fontSize: 11 }}>{new Date(r.created_at).toLocaleDateString('ko-KR')}</span>
                        <span style={{ color: '#4A5568', fontSize: 11 }}>{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </div>
                    <button style={s.btnGhost} onClick={() => handleDelete(r.id)}>삭제</button>
                  </div>
                  {isOpen && <StrategyView st={r.strategy} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
